import React, {useEffect, useState} from 'react';
import Swal from 'sweetalert2';
import http from '../../../services/http';
import styles from './styles.module.css';
import {alerta} from "../../../components/Alert";
import {moedaInput, moedaDisplay, parseMoeda} from '../../../utils/moedaInput';
import useArrastarLinhas from '../../../hook/arrastarLinhas';
import IconeMao from '../../../components/IconeMao';

/**
 * Cadastro das apostas do Tradicional: valor mínimo, valor máximo, bloqueio e a
 * ordem em que elas aparecem no app do cambista — tudo na mesma linha.
 *
 * As três coisas eram duas telas e viraram uma porque editam a MESMA lista
 * (`bancas.modalidades`) e uma mexe na outra: bloquear uma aposta tira ela do app,
 * e a posição só faz sentido ao lado do que está ligado. Em telas separadas a banca
 * bloqueava numa e só descobria o efeito na outra.
 *
 * A ordem daqui vale para as duas telas do app (vender e cotação). Não confundir com
 * a ordem dos CARDS da tela de venda (Tradicional, Seninha, Bilhetinho, Rifa), que é
 * outra configuração.
 *
 * Enquanto a banca não salvar nada ela segue a ordem padrão da Setebit e acompanha as
 * mudanças que a central fizer. Ao salvar, passa a ter ordem própria e ignora o
 * padrão — "Restaurar ordem padrão" desfaz isso.
 */

// Apostas que nenhuma tela de configuração da banca mostra: a Milhar Brinde é ligada
// em Configuração e a Milhar Moto é cadastro legado. Elas continuam na ordem gravada
// (são enviadas no salvar, na posição em que já estavam) — só não aparecem aqui.
// Esconder sem mandar apagaria a posição delas a cada salvamento.
const naoMostrar = [
    'Milhar Brinde', 'Milhar Moto', 'Unidade'
];

// Frações (1/2, 1/5, 1/10...) não têm valor próprio: herdam o da aposta cheia e só
// podem ser bloqueadas.
const submodalidades = [
    'Terno de Grupo 1/3',
    'Terno de Grupo 1/5', 'Terno de Grupo 1/10', 'Passe 1/2',
    'Passe 1/5', 'Passe vai e vem 1/2', 'Passe vai e vem 1/5',
    'Duque de Grupo 1/2', 'Duque de Grupo 1/5', 'Duque de Dezena Combinado  1/2',
    'Duque de Dezena Combinado 1/5', 'Terno de Dezena Combinado  1/3', 'Terno de Dezena Combinado 1/5',
    'Terno de Grupo Combinado  1/3', 'Terno de Grupo Combinado 1/5', 'Duque de Dezena 1/2', 'Duque de Dezena 1/5',
    'Duque de Dezena F.M.F 1/2', 'Duque de Dezena F.M.F 1/5',
    'Terno de Dezena 1/3', 'Terno de Dezena 1/5', 'Terno de Dezena 1/10',
    'Terno de Dezena F.M.F 1/3', 'Terno de Dezena F.M.F 1/5', 'Terno de Dezena F.M.F 1/10'
];

// O cadastro tem nome com espaço duplicado ("Terno de Grupo Combinado  1/3"). Comparar
// sempre normalizado é o que impede uma fração escapar da lista acima por um espaço.
const normalizar = (nome) => (nome || '').replace(/\s+/g, ' ').trim();

const submodalidadesNormalizadas = submodalidades.map(normalizar);

const ehSubmodalidade = (nome) => submodalidadesNormalizadas.includes(normalizar(nome));

const ehOculta = (nome) => naoMostrar.includes(normalizar(nome));

const nomeExibicao = (nome) =>
    normalizar(nome).replace(/duque\s*de\s*grupo\s*combinado/gi, 'Grupo Combinado');

export default function Modalidades() {

    const [modalidadesBanca, setModalidadesBanca] = useState([]);
    // Ordem como está gravada: lista de `tipo`, INCLUSIVE os que a tela esconde.
    const [ordemTipos, setOrdemTipos] = useState([]);
    const [personalizada, setPersonalizada] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isActiveFieldsEditModalidade, setIsActiveFieldsEditModalidade] = useState(true);
    const [carregaPost, setCarregaPost] = useState(false);

    /**
     * Duas requisições porque são dois recortes da mesma lista: o cadastro (com valor,
     * bloqueio e tudo que a tela precisa devolver intacto no salvar) e a ordem
     * resolvida. O salvar, esse sim, é um só.
     */
    async function carregar() {
        try {
            const [respostaModalidades, respostaOrdem] = await Promise.all([
                http.get('/bancas/modalidades'),
                http.get('/bancas/modalidades/tipos/ordem'),
            ]);

            const {data: modalidades} = respostaModalidades.data;
            const {data: ordem} = respostaOrdem.data;

            // Normaliza os valores monetários (API usa ponto: "10.50") para o formato de
            // exibição com vírgula ("10,50") já no carregamento. Assim o state fica consistente
            // com o que moedaInput produz no onChange, os inputs exibem o formato certo e
            // parseMoeda() converte corretamente ao salvar — inclusive campos não editados.
            setModalidadesBanca(
                modalidades.map((m) => ({
                    ...m,
                    valor_minimo: moedaDisplay(m.valor_minimo),
                    valor_maximo: moedaDisplay(m.valor_maximo),
                }))
            );
            setOrdemTipos((ordem.tipos_aposta || []).map((tipoAposta) => tipoAposta.tipo));
            setPersonalizada(!!ordem.personalizada);
            setIsLoading(false);
        } catch (e) {
            setIsLoading(false);
            alerta(e.response.data);
        }
    }

    useEffect(() => {
        carregar();
    }, []);

    function atualizarEstado(index, campo, element) {
        // Updater imutável: lê sempre o state mais recente (sem closure stale),
        // cria um objeto NOVO só para o item alterado (React reconcilia o input)
        // e mantém os demais intactos.
        setModalidadesBanca(prev =>
            prev.map((m, i) =>
                i === index ? { ...m, [campo]: element } : m
            )
        );
    }

    /**
     * As linhas da tela, já na ordem do app.
     *
     * `indice` é a posição no cadastro (é por ele que o valor e o bloqueio são
     * editados) e `indiceOrdem` é a posição na ordem gravada (é por ele que a linha se
     * move). Os dois são diferentes de propósito: o cadastro nunca é reordenado, só a
     * ordem é — assim o JSON gravado continua na sequência em que a banca nasceu.
     */
    const indicePorTipo = new Map();

    modalidadesBanca.forEach((modalidade, indice) => {
        if (modalidade.tipo) {
            indicePorTipo.set(modalidade.tipo, indice);
        }
    });

    const linhas = [];

    ordemTipos.forEach((tipo, indiceOrdem) => {
        const indice = indicePorTipo.get(tipo);

        if (indice === undefined || ehOculta(modalidadesBanca[indice].nome)) {
            return;
        }

        linhas.push({modalidade: modalidadesBanca[indice], indice, indiceOrdem});
    });

    // Cadastro antigo sem `tipo` não tem como ser ordenado (o `tipo` é a identidade da
    // aposta), mas continua editável no fim da lista em vez de sumir da tela.
    modalidadesBanca.forEach((modalidade, indice) => {
        if (modalidade.tipo || ehOculta(modalidade.nome)) {
            return;
        }

        linhas.push({modalidade, indice, indiceOrdem: null});
    });

    const ordenaveis = linhas
        .filter((linha) => linha.indiceOrdem !== null)
        .map((linha) => linha.indiceOrdem);

    /**
     * Tira o tipo de onde ele está e enfia na posição de destino, empurrando o resto.
     * Os tipos escondidos ficam onde estão e são carregados junto — é o que preserva a
     * posição deles sem ocupar número na tela.
     */
    function moverPara(indiceOrdem, destino) {
        setOrdemTipos(prev => {
            const lista = [...prev];
            const [movido] = lista.splice(indiceOrdem, 1);

            lista.splice(destino, 0, movido);

            return lista;
        });
    }

    /**
     * Arrastar pela mãozinha. O hook devolve posições da TELA; convertidas para a
     * lista gravada aqui, soltar sobre uma linha visível encaixa o tipo no lugar dela
     * mesmo quando há tipos escondidos no meio.
     */
    const arraste = useArrastarLinhas((origem, destino) => {
        moverPara(ordenaveis[origem], ordenaveis[destino]);
    });

    /**
     * Move a linha para a posição digitada. Reordenar na hora (em vez de só guardar o
     * número) é o que deixa a tela mostrar o resultado antes de salvar.
     */
    function irParaPosicao(indiceOrdem, valor) {
        const posicao = parseInt(valor, 10);

        if (!posicao || posicao < 1 || posicao > ordenaveis.length) {
            return;
        }

        moverPara(indiceOrdem, ordenaveis[posicao - 1]);
    }

    /**
     * Um PATCH só com as duas coisas. Em duas requisições existiria o estado em que a
     * ordem gravou e o bloqueio não — a tela diria uma coisa e o app faria outra.
     */
    async function salvar() {
        try {
            setCarregaPost(true);

            const modalidadesParaEnviar = modalidadesBanca.map((m) => ({
                ...m,
                valor_minimo: parseMoeda(m.valor_minimo),
                valor_maximo: parseMoeda(m.valor_maximo),
            }));

            const corpo = {modalidades: modalidadesParaEnviar};

            if (ordemTipos.length > 0) {
                corpo.ordem = ordemTipos.map((tipo, indice) => ({tipo, posicao: indice + 1}));
            }

            const res = await http.patch('/bancas/modalidades', corpo);

            alerta(res.data);

            setIsActiveFieldsEditModalidade(true);
            setCarregaPost(false);
            carregar();
        } catch (e) {
            setCarregaPost(false);
            alerta(e.response.data);
        }
    }

    function perguntaRestaurar() {
        Swal.fire({
            title: 'Atenção!',
            text: 'A ordem que você montou será apagada e as apostas voltam a aparecer na ordem padrão do sistema. Alterações de valor ainda não salvas serão perdidas. Deseja continuar?',
            type: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            reverseButtons: true,
            confirmButtonText: 'Confirmar',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (result.value) {
                restaurarPadrao();
            }
        });
    }

    async function restaurarPadrao() {
        try {
            setCarregaPost(true);

            const res = await http.delete('/bancas/modalidades/tipos/ordem');

            alerta(res.data);

            setCarregaPost(false);
            carregar();
        } catch (e) {
            setCarregaPost(false);
            alerta(e.response.data);
        }
    }

    function handleClickEditModalidade() {
        setIsActiveFieldsEditModalidade(!isActiveFieldsEditModalidade);
    }

    return (
        <>
            <div className="titulo-principal">Modalidades</div>

            {isLoading && <div className='loader'/>}

            {!isLoading && linhas.length > 0 && (
                <div className='mt-3'>
                    <div className='bordered'>

                        <div style={{padding: '10px'}}>
                            <p className={styles.ajuda}>
                                Valor mínimo, valor máximo e bloqueio de cada aposta do Tradicional,
                                e a ordem em que elas aparecem no aplicativo do cambista — tanto na
                                hora de vender quanto na tabela de cotação. Quem está em cima aparece
                                primeiro. Arraste pela mãozinha ou digite o número da posição.
                            </p>

                            {personalizada ? (
                                <div className='alert alert-info mb-0'>
                                    Esta banca usa uma ordem própria. Mudanças na ordem padrão do
                                    sistema não afetam mais o seu aplicativo.
                                </div>
                            ) : (
                                <div className='alert alert-secondary mb-0'>
                                    Esta banca está usando a ordem padrão do sistema. Ao salvar, ela
                                    passa a ter uma ordem própria.
                                </div>
                            )}
                        </div>

                        <div className={`table-responsive ${styles.lista}`}>
                            <table className='table'>
                                <thead>
                                <tr>
                                    <th className={styles.colunaPosicao}>Posição</th>
                                    <th className={styles.colunaMover}>Mover</th>
                                    <th>Modalidade</th>
                                    <th>Valor Minimo</th>
                                    <th>Valor Maximo</th>
                                    <th>Status</th>
                                </tr>
                                </thead>
                                <tbody>
                                {linhas.map(({modalidade, indice, indiceOrdem}, posicao) => {
                                    const submodalidade = ehSubmodalidade(modalidade.nome);
                                    const ordenavel = indiceOrdem !== null;

                                    return (
                                        <tr
                                            key={modalidade.tipo || `sem-tipo-${indice}`}
                                            className={[
                                                modalidade.ativo ? '' : styles.bloqueada,
                                                ordenavel && arraste.arrastando === posicao ? styles.arrastando : '',
                                                ordenavel && arraste.alvo === posicao && arraste.arrastando !== posicao
                                                    ? (posicao > arraste.arrastando ? styles.alvoAbaixo : styles.alvoAcima)
                                                    : '',
                                            ].join(' ').trim()}
                                            {...(ordenavel ? arraste.linha(posicao) : {})}
                                        >
                                            <td className={styles.colunaPosicao}>
                                                {ordenavel ? (
                                                    <input
                                                        type='number'
                                                        min='1'
                                                        max={ordenaveis.length}
                                                        className='form-control'
                                                        disabled={carregaPost}
                                                        value={posicao + 1}
                                                        onChange={(event) => irParaPosicao(indiceOrdem, event.target.value)}
                                                    />
                                                ) : (
                                                    <span className='text-muted'>---</span>
                                                )}
                                            </td>

                                            <td className={styles.colunaMover}>
                                                {ordenavel && (
                                                    <span
                                                        className={styles.alca}
                                                        title='Arraste para mudar a posição'
                                                        {...arraste.alca(posicao, carregaPost)}
                                                    >
                                                        <IconeMao/>
                                                    </span>
                                                )}
                                            </td>

                                            <td>{nomeExibicao(modalidade.nome)}</td>

                                            {submodalidade ? (
                                                <td>
                                                    <input
                                                        type='text'
                                                        className='form-control'
                                                        disabled
                                                        value="---"
                                                    />
                                                </td>
                                            ) : (
                                                <td>
                                                    <input
                                                        onChange={(event) =>
                                                            atualizarEstado(
                                                                indice,
                                                                'valor_minimo',
                                                                moedaInput(event.target.value)
                                                            )
                                                        }
                                                        type='text'
                                                        inputMode='numeric'
                                                        className='form-control'
                                                        disabled={isActiveFieldsEditModalidade}
                                                        value={modalidade.valor_minimo}
                                                        placeholder='0,00'
                                                    />
                                                </td>
                                            )}

                                            {submodalidade ? (
                                                <td>
                                                    <input
                                                        type='text'
                                                        className='form-control'
                                                        disabled
                                                        value="---"
                                                    />
                                                </td>
                                            ) : (
                                                <td>
                                                    <input
                                                        onChange={(event) =>
                                                            atualizarEstado(
                                                                indice,
                                                                'valor_maximo',
                                                                moedaInput(event.target.value)
                                                            )
                                                        }
                                                        type='text'
                                                        inputMode='numeric'
                                                        className='form-control'
                                                        disabled={isActiveFieldsEditModalidade}
                                                        value={modalidade.valor_maximo}
                                                        placeholder='0,00'
                                                    />
                                                </td>
                                            )}

                                            {!submodalidade ? (
                                                <td>
                                                    <button
                                                        onClick={() => atualizarEstado(indice, 'ativo', !modalidade.ativo)}
                                                        className={`btn ${
                                                            modalidade.ativo ? 'btn-success' : 'btn-danger'
                                                        }`}
                                                        disabled={carregaPost}
                                                    >
                                                        {modalidade.ativo ? 'Ativa' : 'Bloqueada'}
                                                    </button>
                                                </td>
                                            ) : (
                                                <td>
                                                    <button
                                                        onClick={() => atualizarEstado(indice, 'ativo', !modalidade.ativo)}
                                                        className={`${modalidade.ativo ? 'text-success' : 'text-danger'} font-weight-bold text-decoration-underline`}
                                                        disabled={carregaPost}
                                                    >
                                                        {modalidade.ativo ? 'Desbloqueado' : 'Bloqueado'}
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>

                        <div className='d-flex duas-colunas' style={{padding: '10px'}}>
                            {isActiveFieldsEditModalidade ? (
                                <button
                                    className='btn btn-info'
                                    onClick={() => handleClickEditModalidade()}
                                    disabled={carregaPost}
                                >
                                    Editar valores
                                </button>
                            ) : (
                                <button
                                    className='btn btn-secondary'
                                    onClick={() => handleClickEditModalidade()}
                                    disabled={carregaPost}
                                >
                                    Desabilitar
                                </button>
                            )}

                            {personalizada && (
                                <button
                                    className='btn btn-secondary'
                                    onClick={() => perguntaRestaurar()}
                                    disabled={carregaPost}
                                >
                                    Restaurar ordem padrão
                                </button>
                            )}

                            <button
                                className='btn btn-success'
                                onClick={() => salvar()}
                                disabled={carregaPost}
                            >
                                Salvar
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
}
